import React, { useState, useEffect, useRef } from 'react';
import White from '../../assets/icons/popup/coin wallet.svg?react';
import Withdraw from '../../assets/icons/Withdraw.svg?react';
import BgLatto from '../../assets/images/bg-latto.jpg';
import Wallet from '../../assets/icons/popup/input-walet.svg?react';
import Spinner from './Spinner';
import DepositSuccessPopup from './DepositSuccessPopup';
import DepositConfirmationPopup from './DepositConfirmationPopup';
import payApi from '../../api/pay';
import { useAuth } from '../../context/AuthContext';
import { useWebSocket } from '../../context/WebSocketContext';
import Swal from 'sweetalert2';

import { settingsApi } from "../../api/settings";

// Import Gateway Logos
import GCashLogo from "../../assets/icons/popup/GCASH-Logo.svg?react";
import MayaLogo from "../../assets/icons/popup/MAYA-Logo.svg?react";
import QRPHLogo from "../../assets/icons/popup/QR_Ph_Logo.svg?react";

const PAYMENT_METHODS_CONFIG = {
  gcash: {
    label: "GCash",
    Logo: GCashLogo,
    apiCallKey: 'pay', // Key to map to payApi functions
    defaultMinAmount: 50,
  },
  maya: {
    label: "Maya",
    Logo: MayaLogo,
    apiCallKey: 'payMaya',
    defaultMinAmount: 50,
  },
  qrph: {
    label: "QRPH",
    Logo: QRPHLogo,
    apiCallKey: 'pay', // Assuming QRPH uses the same generic 'pay' endpoint
    defaultMinAmount: 2000,
  },
};

export default function DepositPopupQR({ isOpen, onClose, currentBalance }) {
  const [amount, setAmount] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { user, tokens } = useAuth();
  const { resetPaymentStatus } = useWebSocket();
  const [paymentSuccessData, setPaymentSuccessData] = useState([]);
  
  const [availableGateways, setAvailableGateways] = useState([]);
  const [gatewayMinAmounts, setGatewayMinAmounts] = useState({});
  const [isLoadingGateways, setIsLoadingGateways] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [showGatewaySelection, setShowGatewaySelection] = useState(false);

  const getCurrentMinAmount = () => {
    if (selectedGateway) {
      return gatewayMinAmounts[selectedGateway] || PAYMENT_METHODS_CONFIG[selectedGateway]?.defaultMinAmount || 50;
    }
    
    return 50;
  };

  const wasOpenRef = useRef(false);
  useEffect(() => {
    const fetchGateways = async () => {
      if (isOpen && tokens?.access?.token && user?.id) {
        setIsLoadingGateways(true);
        try {
          const response = await settingsApi.getSettingsByKey(tokens.access.token, 'activePaymentGateway');
          const otherSettings = response.data?.otherSettings || response.data?.other_settings;
          const activeGatewaysList = otherSettings?.gateways;
          
          if (Array.isArray(activeGatewaysList) && activeGatewaysList.length > 0) {
            const supportedGateways = activeGatewaysList.filter(gw => PAYMENT_METHODS_CONFIG[gw]);
            setAvailableGateways(supportedGateways);
            
            const minAmounts = {};
            supportedGateways.forEach(gw => {
              minAmounts[gw] = otherSettings?.minAmounts?.[gw] || PAYMENT_METHODS_CONFIG[gw]?.defaultMinAmount || 50;
            });
            setGatewayMinAmounts(minAmounts);
            
            if (supportedGateways.length === 0 && activeGatewaysList.length > 0) {
                Swal.fire('Configuration Issue', 'Some payment methods returned by the server are not configured in the UI.', 'warning');
            }
          } else {
            setAvailableGateways([]);
            setGatewayMinAmounts({});
          }
        } catch (error) {
          console.error("Failed to fetch payment gateways:", error);
          setAvailableGateways([]);
          setGatewayMinAmounts({});
          // Swal.fire('Error', 'Could not load payment methods. Please try again later.', 'error');
        } finally {
          setIsLoadingGateways(false);
        }
      }
    };

    if (isOpen && !wasOpenRef.current) {
      resetPaymentStatus();
      setAmount(50);
      setSelectedGateway(null);
      setShowGatewaySelection(true);
      setShowConfirmation(false);
      setShowSuccess(false);
      fetchGateways();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, tokens, user, resetPaymentStatus]);

  useEffect(() => {
    if (selectedGateway) {
      const minAmount = getCurrentMinAmount();
      setAmount(minAmount);
    }
  }, [selectedGateway]);

  if (!isOpen) return null;

  const handleActualDeposit = async () => {
    if (!selectedGateway) {
      Swal.fire('Error', 'No payment gateway selected.', 'error');
      return;
    }

    setShowConfirmation(false); // Close confirmation before loading
    setIsLoading(true);
    const data = {
      amount: amount,
      currency: 'PHP',
      name: user.name,
      email: user.email,
      gateway: selectedGateway,
    };

    try {
      let response;
      const methodConfig = PAYMENT_METHODS_CONFIG[selectedGateway];

      if (!methodConfig) {
        throw new Error('Invalid payment method configuration.');
      }

      if (methodConfig.apiCallKey === 'payMaya') {
        response = await payApi.payMaya(tokens.access.token, data);
      } else if (methodConfig.apiCallKey === 'pay') {
        // This assumes payApi.pay is for GCash/QRPH or a generic HitPay like service
        response = await payApi.pay(tokens.access.token, data);
      } else {
        throw new Error(`No API call defined for ${selectedGateway}`);
      }
      
      if (response.status === 200) {
        setPaymentSuccessData(response.data);
        setShowSuccess(true);
      } else {
        Swal.fire('Payment Failed', response.data?.message || 'The payment could not be processed.', 'error');
      }
    } catch (error) {
      console.error("Payment API call error:", error);
      Swal.fire('Payment Error', error.message || 'An unexpected error occurred during payment.', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDepositButtonClick = () => {
    const numericAmount = parseFloat(amount);
    const minAmount = getCurrentMinAmount();
    
    if (isNaN(numericAmount) || numericAmount < minAmount) {
      Swal.fire({
        title: 'Invalid Amount',
        text: `Amount must be at least ₱${minAmount.toLocaleString()}.`,
        icon: 'error',
      });
      return;
    }

    // Since we already have a selected gateway, go directly to confirmation
    setShowConfirmation(true);
  };

  const handleGatewaySelect = (gatewayKey) => {
    setSelectedGateway(gatewayKey);
    setShowGatewaySelection(false);
    // Don't go to confirmation yet, show amount input instead
  };

  const handleClose = () => {
    setShowSuccess(false); // Close success popup
    onClose(); // Call original onClose to close the main deposit modal
  };

  if (isLoading) { // Full screen spinner for critical operations
    return (
      <div className='fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60] font-poppins'>
        <Spinner />
      </div>
    );
  }
  
  if (showSuccess) {
    return (
      <DepositSuccessPopup
        amount={amount}
        onClose={handleClose} // Use updated handleClose
        paymentSuccessData={paymentSuccessData}
      />
    );
  }

  if (showConfirmation) {
    return (
      <DepositConfirmationPopup
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleActualDeposit} // Use the renamed function
        amount={amount}
        gatewayName={PAYMENT_METHODS_CONFIG[selectedGateway]?.label || selectedGateway}
      />
    );
  }

  if (showGatewaySelection) {
    return (
      <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 font-poppins'>
        <div className='animate-slideInAndShake relative w-full max-w-md bg-gradient-to-br from-slate-800 to-slate-900 shadow-2xl rounded-2xl transform transition-all duration-300'>
          <div
            className='absolute inset-0 opacity-20'
            style={{
              backgroundImage: `url('${BgLatto}')`,
              backgroundBlendMode: 'multiply',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          ></div>
          <div className='relative p-6'>
            <button
              onClick={onClose} // Close the entire popup
              className='absolute right-3 top-3 text-gray-400 hover:text-white z-10'
              aria-label='Close'
            >
              <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' /></svg>
            </button>
            <h3 className="text-2xl font-semibold text-white mb-6 text-center">Select Payment Method</h3>
            {isLoadingGateways ? (
                <div className="flex justify-center items-center h-32">
                    <Spinner />
                </div>
            ) : availableGateways.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {availableGateways.map((key) => {
                  const config = PAYMENT_METHODS_CONFIG[key];
                  if (!config) return null; // Should not happen if filtered correctly
                  const { Logo, label } = config;
                  const minAmount = gatewayMinAmounts[key] || config.defaultMinAmount || 50;
                  return (
                    <button
                      key={key}
                      onClick={() => handleGatewaySelect(key)}
                      className="bg-white flex flex-col items-center justify-center p-4 rounded-xl border border-gray-700 bg-gray-800/50 transition-all duration-200 shadow-lg"
                    >
                      <Logo className="w-16 h-16 mb-2 text-white" />
                      <span className="font-medium text-gray-500 text-sm">{label}</span>
                      <span className="text-xs text-gray-500 mt-1">Min: ₱{minAmount.toLocaleString()}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
                <p className="text-gray-400 text-center py-4">No payment methods available.</p>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Main Deposit Form (Amount Input) - Only show if gateway is selected
  if (selectedGateway && !isLoadingGateways) {
    const selectedConfig = PAYMENT_METHODS_CONFIG[selectedGateway];
    const { Logo, label } = selectedConfig;
    
    return (
      <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 font-poppins'>
        <div className='animate-slideInAndShake relative w-full max-w-sm overflow-hidden rounded-2xl transform transition-all duration-300'>
          {/* Background with gradient */}
          <div
            className='absolute inset-0 '
            style={{
              backgroundImage: `url('${BgLatto}'), linear-gradient(108.9deg, rgba(14, 100, 209, 0.6) 0%, rgba(30, 66, 154, 0.6) 100.79%)`,
              backgroundBlendMode: 'multiply',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          ></div>

          {/* Content */}
          <div className='relative p-4'>
            <button
              onClick={onClose}
              className='absolute right-2 top-2 text-white/80 hover:text-white'
              aria-label='Close'
            >
              <svg
                className='w-5 h-5'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M6 18L18 6M6 6l12 12'
                />
              </svg>
            </button>

            {/* Selected Payment Method Display */}
            <div className='mb-4 flex items-center justify-center text-white'>
              <button
                onClick={() => {
                  setSelectedGateway(null);
                  setShowGatewaySelection(true);
                }}
                className='flex items-center gap-2 bg-white/20 rounded-lg px-3 py-2 hover:bg-white/30 transition-colors'
              >
                <Logo className="w-6 h-6" />
                <span className="font-medium">{label}</span>
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                </svg>
              </button>
            </div>

            {/* Balance display */}
            <div className='mb-3 flex justify-center items-center text-white'>
              <White />
              <div className='flex flex-col'>
                <h5 className='text-[10px] leading-none font-normal'>
                  Current Balance
                </h5>
                <h3 className='text-4xl font-semibold'>
                  {currentBalance.toLocaleString()}
                </h3>
              </div>
            </div>

            {/* Amount input */}
            <div className='mb-6 relative'>
              <input
                type='number'
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className='w-full bg-white rounded-lg py-3 px-4 pl-12 text-lg font-normal text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                placeholder={`Enter amount (min ₱${getCurrentMinAmount().toLocaleString()})`}
              />
              <Wallet className='absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400' />
            </div>

            {/* Deposit Button */}
            <button
              onClick={handleDepositButtonClick}
              className='w-full bg-[#0E64D1] hover:bg-blue-600 text-white py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2'
            >
              <Withdraw />
              DEPOSIT
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If no gateway selected and not loading, return null (gateway selection will show)
  return null;
}
